/**
 * GPS interference overlay, ported from loader-gpsjam.js: gpsjam.org hex cells
 * for the current UTC day as orange (#ff5722) polygons with alpha equal to the
 * interference fraction capped at 0.6, cells below 5% skipped, refreshed hourly
 * while enabled. A feed failure warns, toasts and switches the layer off, as
 * the original unchecked its toggle.
 */
import { toast } from '@/components/ui/toast';
import type { GpsJamPolygon } from '../globe/GlobeEngine';
import { useLoader } from '../store';
import { getGpsJam, type GpsJamFeature, type GpsJamResponse } from './feeds';
import { useAwareness } from './store';
import { startPolling } from './util';

export const GPSJAM_REFRESH_MS = 60 * 60 * 1000;
export const INTERFERENCE_THRESHOLD = 0.05;
export const MAX_ALPHA = 0.6;
export const GPSJAM_COLOR = '#ff5722';

/** The feature list of a FeatureCollection, or the bare array form. */
export function gpsJamFeatures(data: GpsJamResponse | null | undefined): GpsJamFeature[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Array.isArray(data.features) ? data.features : [];
}

/** Polygons for cells at or above the threshold: outer ring flattened to [lon, lat, ...], alpha = min(0.6, interference). */
export function gpsJamPolygons(data: GpsJamResponse | null | undefined): GpsJamPolygon[] {
  const out: GpsJamPolygon[] = [];
  gpsJamFeatures(data).forEach((feature, idx) => {
    if (!feature || !feature.geometry || !feature.properties) return;
    const interference = feature.properties.interference || feature.properties.bad || 0;
    if (interference < INTERFERENCE_THRESHOLD) return;
    const ring = feature.geometry.coordinates?.[0];
    if (!ring) return;
    const positions: number[] = [];
    for (const point of ring) positions.push(point[0], point[1]);
    out.push({ id: String(idx), positions, alpha: Math.min(MAX_ALPHA, interference) });
  });
  return out;
}

export async function refreshGpsJam(): Promise<void> {
  try {
    const data = await getGpsJam();
    useAwareness.getState().setGpsJam(gpsJamPolygons(data));
  } catch (err) {
    console.warn('[gpsjam] gpsjam.org feed unavailable:', err);
    toast('warn', 'GPS jamming overlay unavailable; the layer has been switched off.');
    useAwareness.getState().setGpsJam(null);
    if (useLoader.getState().gpsJamEnabled) useLoader.getState().setView({ gpsJamEnabled: false });
  }
}

/** Refresh now and hourly until stopped; the polygons are cleared on stop. */
export function startGpsJam(): () => void {
  const stop = startPolling(() => {
    void refreshGpsJam();
  }, GPSJAM_REFRESH_MS);
  return () => {
    stop();
    useAwareness.getState().setGpsJam(null);
  };
}
